import React, { useEffect } from 'react';
import { RefreshCw, CheckCircle2, AlertCircle, X, Upload } from 'lucide-react';
import { useOfflineStore } from '../../../store/useOfflineStore';
import { useKanbanStore } from '../../../store/useKanbanStore';

interface Props {
  activeProjectId: string;
  onSyncComplete: () => void;
}

export const SyncProgressBar: React.FC<Props> = ({ activeProjectId, onSyncComplete }) => {
  const { syncProgress, pendingCounts, syncBoard, refreshPendingCounts, dismissSyncProgress } =
    useOfflineStore();

  const currentUser = useKanbanStore((s: any) => s.currentUser);

  const pendingCount = pendingCounts[activeProjectId] ?? 0;
  const isActiveSyncForThisBoard =
    syncProgress.boardId === activeProjectId && syncProgress.phase !== 'idle';

  useEffect(() => {
    if (activeProjectId) {
      void refreshPendingCounts();
    }
  }, [activeProjectId, refreshPendingCounts]);

  // After sync finishes, refresh store data
  useEffect(() => {
    if (syncProgress.phase === 'done' && syncProgress.boardId === activeProjectId) {
      onSyncComplete();
    }
  }, [syncProgress.phase, syncProgress.boardId, activeProjectId, onSyncComplete]);

  if (!activeProjectId || !currentUser) return null;
  if (pendingCount === 0 && !isActiveSyncForThisBoard) return null;

  const handleSync = () => {
    void syncBoard(activeProjectId);
  };

  const progressPct =
    syncProgress.total > 0
      ? Math.round((syncProgress.current / syncProgress.total) * 100)
      : 0;

  return (
    <div className="relative bg-indigo-50 dark:bg-indigo-950/40 border-b border-indigo-200 dark:border-indigo-800 px-4 py-2 flex items-center gap-3 text-xs">
      {/* Progress fill */}
      {syncProgress.phase === 'pushing' && syncProgress.total > 0 && (
        <div
          className="absolute inset-0 bg-indigo-100 dark:bg-indigo-900/40 transition-all duration-300 origin-left"
          style={{ transform: `scaleX(${progressPct / 100})` }}
          aria-hidden="true"
        />
      )}

      <div className="relative flex flex-1 items-center gap-2 min-w-0">
        {syncProgress.phase === 'pushing' ? (
          <>
            <RefreshCw className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400 animate-spin shrink-0" />
            <span className="text-indigo-800 dark:text-indigo-300 font-medium truncate">
              Syncing {syncProgress.current} of {syncProgress.total} changes
              {syncProgress.total > 0 && ` (${progressPct}%)`}
            </span>
            <span className="text-indigo-600 dark:text-indigo-400 hidden sm:inline">
              — you can keep working, new changes queue behind
            </span>
          </>
        ) : syncProgress.phase === 'done' && syncProgress.boardId === activeProjectId ? (
          <>
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
            <span className="text-emerald-800 dark:text-emerald-300 font-medium">
              Sync complete — board is up to date
            </span>
          </>
        ) : syncProgress.phase === 'error' && syncProgress.boardId === activeProjectId ? (
          <>
            <AlertCircle className="w-3.5 h-3.5 text-red-600 dark:text-red-400 shrink-0" />
            <span className="text-red-800 dark:text-red-300 font-medium">
              {syncProgress.errorMessage ?? 'Sync failed'}
            </span>
          </>
        ) : (
          <>
            <Upload className="w-3.5 h-3.5 text-indigo-500 dark:text-indigo-400 shrink-0" />
            <span className="text-indigo-800 dark:text-indigo-300 font-medium">
              {pendingCount} unsynced change{pendingCount !== 1 ? 's' : ''} on this board
            </span>
          </>
        )}
      </div>

      <div className="relative flex items-center gap-2 shrink-0">
        {syncProgress.phase !== 'pushing' && pendingCount > 0 && (
          <button
            type="button"
            onClick={handleSync}
            disabled={!navigator.onLine}
            className="px-2.5 py-1 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={navigator.onLine ? 'Sync now' : 'You are offline'}
          >
            Sync now
          </button>
        )}
        {(syncProgress.phase === 'done' || syncProgress.phase === 'error') &&
          syncProgress.boardId === activeProjectId && (
            <button
              type="button"
              onClick={dismissSyncProgress}
              className="p-1 rounded text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
              title="Dismiss"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
      </div>
    </div>
  );
};
